import React from 'react';
import { ApolloError } from '@apollo/client';
import { Box, IconButton, LinearProgress, Menu, MenuItem, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  LoadedContent,
  Pagination,
  StatusChip,
  MoreVertIcon,
  MinusIcon,
  EqualsIcon,
  ProgressIndicator,
} from '@occc-app/design-system';
import { SteeringWalletFragment, SteeringWalletStatus } from '@occc/graphql';
import { IPaginationProps } from '@occc-app/hooks/usePaginatedQuery';

import { chipVariantByStatus, titleByBasis, titleByStatus } from '../constants';
import { formatNumberOrPlaceholder } from '../utils';

type WalletsTableProps = {
  wallets: SteeringWalletFragment[] | null | undefined;
  loading: boolean;
  error?: ApolloError;
  paginationProps: IPaginationProps;
  isUserPermittedToEditWallet: boolean;
  isUserPermittedToArchiveWallet: boolean;
  isUserPermittedToViewWalletConfiguration: boolean;
  onEdit: (item: SteeringWalletFragment) => void;
  onArchive: (item: SteeringWalletFragment) => void;
  onViewDetails: (item: SteeringWalletFragment) => void;
};

export const WalletsTable: React.FC<WalletsTableProps> = ({
  wallets,
  loading,
  error,
  paginationProps,
  onEdit,
  onArchive,
  onViewDetails,
  isUserPermittedToEditWallet,
  isUserPermittedToArchiveWallet,
}) => {
  const [actionsForUuid, setActionsForUuid] = React.useState<string | null>();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const getHandlerForAdditionalActions =
    (item: SteeringWalletFragment) => (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();

      setAnchorEl(event.currentTarget);
      setActionsForUuid(item.uuid);
    };

  const handleOptionsMenuClose = React.useCallback(() => {
    setAnchorEl(null);
    setActionsForUuid(null);
  }, []);

  const menuItemOnClick = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    doAction: () => void,
  ) => {
    event.stopPropagation();
    doAction();
    handleOptionsMenuClose();
  };
  const theme = useTheme();

  const renderMoreOptionsMenu = (item: SteeringWalletFragment) => {
    const isArchiveItemVisible =
      isUserPermittedToArchiveWallet && item.status !== SteeringWalletStatus.Archived;
    const isEditConfigurationItemVisible =
      isUserPermittedToEditWallet && item.status !== SteeringWalletStatus.Archived;

    // TODO: menu with 3 dots is already used in multiple places and
    // it's a good candidate to be a separate component
    return (
      <Menu
        open={item.uuid === actionsForUuid}
        onClose={(event) => {
          (event as any).stopPropagation();
          handleOptionsMenuClose();
        }}
        anchorEl={anchorEl}
        PaperProps={{
          sx: {
            borderRadius: '4px',
          },
        }}
      >
        <MenuItem
          onClick={(event) =>
            menuItemOnClick(event, () => {
              onViewDetails(item);
            })
          }
        >
          View Details
        </MenuItem>

        {isEditConfigurationItemVisible && (
          <MenuItem
            onClick={(event) =>
              menuItemOnClick(event, () => {
                onEdit(item);
              })
            }
          >
            Edit Wallet
          </MenuItem>
        )}
        {isArchiveItemVisible && (
          <MenuItem
            onClick={(event: React.MouseEvent<HTMLLIElement, MouseEvent>) =>
              menuItemOnClick(event, () => {
                onArchive(item);
              })
            }
          >
            Archive Wallet
          </MenuItem>
        )}
      </Menu>
    );
  };

  const renderData = (items: SteeringWalletFragment[]) => {
    return (
      <>
        {loading && <LinearProgress />}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Wallet</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Members</TableCell>
              <TableCell>
                <Box display='flex' flexDirection='row' justifyContent='center' alignItems='center'>
                  <Box>Wallet Capacity</Box>
                  <Box
                    ml='auto'
                    mr='auto'
                    display='flex'
                    justifyContent='center'
                    alignItems='center'
                  >
                    <MinusIcon />
                  </Box>
                </Box>
              </TableCell>
              <TableCell>
                <Box display='flex' flexDirection='row' justifyContent='center' alignItems='center'>
                  <Box>Share of Wallet</Box>
                  <Box
                    ml='auto'
                    mr='auto'
                    display='flex'
                    justifyContent='center'
                    alignItems='center'
                  >
                    <EqualsIcon />
                  </Box>
                </Box>
              </TableCell>
              <TableCell>Wallet Opportunity</TableCell>
              {/* table cell for opportunity indicator */}
              <TableCell />
              {/* table cell for three dots */}
              <TableCell />
            </TableRow>
          </TableHead>

          <TableBody>
            {items.map((item, index) => (
              <TableRow
                key={item.uuid}
                onClick={(event) => {
                  event.stopPropagation();
                  onViewDetails(item);
                }}
              >
                <TableCell>
                  <Box display='flex' flexDirection='row' alignItems='center'>
                    <Typography variant='subtitle2'>{item.name}</Typography>
                    {item.configuration.basis && (
                      <Box ml={0.5}>{`(${titleByBasis[item.configuration.basis]})`}</Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <StatusChip
                    label={titleByStatus[item.status]}
                    variant={chipVariantByStatus[item.status]}
                  />
                </TableCell>
                <TableCell>{item.statistics.membershipCount}</TableCell>
                <TableCell>{formatNumberOrPlaceholder(item.statistics.walletCapacity)}</TableCell>
                <TableCell>{formatNumberOrPlaceholder(item.statistics.walletShare)}</TableCell>
                <TableCell>
                  <Typography variant='subtitle2'>
                    {formatNumberOrPlaceholder(item.statistics.walletOpportunity)}
                  </Typography>
                </TableCell>
                <TableCell>
                  {item.statistics.walletCapacity > 0 && (
                    <Box display='flex' justifyContent='center' alignItems='center'>
                      <ProgressIndicator
                        color={
                          theme.palette.WalletSteeringOpportunityIndicatorColors[
                            index % theme.palette.WalletSteeringOpportunityIndicatorColors.length
                          ]
                        }
                        current={item.statistics.walletShare ?? 0}
                        total={item.statistics.walletCapacity}
                      />
                    </Box>
                  )}
                </TableCell>
                <TableCell align='right'>
                  <IconButton onClick={getHandlerForAdditionalActions(item)} sx={{ padding: 0 }}>
                    <MoreVertIcon />
                  </IconButton>
                  {renderMoreOptionsMenu(item)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Pagination {...paginationProps} />
      </>
    );
  };

  return (
    <LoadedContent<SteeringWalletFragment>
      error={error}
      loading={loading}
      items={wallets}
      renderData={renderData}
    />
  );
};
